<template>
	<swiper
		:direction="'horizontal'"
		:spaceBetween="20"
		:centeredSlides="false"
		:slidesPerView="'auto'"
		:freeMode="{ enabled: false, sticky: false }"
		:mousewheel="true"
		:class="'slider--media'"
	>
		<swiper-slide v-for="f in files" :key="f.file.id" :class="{ 'slide-downloaddisabled': downloadWaiting }">
			<template v-if="f.isNew">
				<TheBox :title="f.file.fileName" :titleLevel="3" :class="'box--newfile'">
					<TheButton type="default" @buttonclick="$emit('removeFromFiles', f)" :onlyIcon="true" class="button--closeround" title="Datei entfernen">
						<el-icon><CloseBold /></el-icon>
					</TheButton>
					<div class="fileInfoContainer">
						<span class="info--upload">{{ texts.formErrors.waitingForUpload }}</span>
					</div>
				</TheBox>
			</template>

			<TheBox v-else-if="isPdf(f.file) || isAudio(f.file)" :title="''" :alignment="'center'" :class="'box--mediapdf'">
				<TheButton
					v-if="allowDeleteFromServer"
					:disabled="deleteWaiting"
					type="default"
					@buttonclick="removeFileFromServer(f)"
					:onlyIcon="true"
					class="button--closeround"
					title="Datei entfernen"
				>
					<el-icon><CloseBold /></el-icon>
				</TheButton>

				<a :href="getShowUrl(f)" target="_blank" title="Datei in neuem Tab öffnen">
					<PdfIcon v-if="isPdf(f.file)" />
					<AudioIcon v-else-if="isAudio(f.file)" />
					<span class="filename">{{ fileNameWithoutExtension(f.file.fileName) }}{{ fileExtension(f.file.fileName) }}</span>
				</a>
			</TheBox>

			<TheBox v-else-if="isImage(f.file)" :title="''" :class="'box--mediaimg'">
				<TheButton
					v-if="allowDeleteFromServer"
					:disabled="deleteWaiting"
					type="default"
					@buttonclick="removeFileFromServer(f)"
					:onlyIcon="true"
					class="button--closeround"
					title="Datei entfernen"
				>
					<el-icon><CloseBold /></el-icon>
				</TheButton>

				<a :href="getShowUrl(f)" target="_blank" title="Datei in neuem Tab öffnen">
					<img :src="f.file.url" :alt="f.file.fileName" />
				</a>
			</TheBox>

			<TheBox v-else-if="isVideo(f.file)" :title="''" :class="'box--mediavideo'">
				<TheButton
					v-if="allowDeleteFromServer"
					:disabled="deleteWaiting"
					type="default"
					@buttonclick="removeFileFromServer(f)"
					:onlyIcon="true"
					class="button--closeround"
					title="Datei entfernen"
				>
					<el-icon><CloseBold /></el-icon>
				</TheButton>

				<a :href="getShowUrl(f)" target="_blank" title="Datei in neuem Tab öffnen">
					<div class="video-container">
						<div class="video-play-icon"></div>
						<video :src="f.file.url" preload="metadata" muted playsinline></video>
					</div>
				</a>
			</TheBox>
		</swiper-slide>
	</swiper>
	<section v-if="downloadable && files.filter((f) => !f.isNew).length > 0">
		<TheButton type="danger" @buttonclick="downloadZip()" :loading="filesDownloading">
			<el-icon><Files /></el-icon>Als ZIP herunterladen
		</TheButton>
	</section>
</template>

<script lang="ts">
import { defineComponent, PropType, computed } from 'vue';
import TheButton from '../TheButton.vue';
import TheBox from '../TheBox.vue';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Files } from '@element-plus/icons-vue';
import { DetailFile, FileDto, FileResponse } from '@/types/types';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import PdfIcon from '../../assets/icons/PdfIcon.vue';
import { texts } from '../../utils/texts';
import { CloseBold } from '@element-plus/icons-vue';
import AudioIcon from '../../assets/icons/AudioIcon.vue';
import { deleteCaseFile } from '@/requests/cases';
import axios from 'axios';
import { ElMessage } from 'element-plus';
import { getShowUrl, isImage, isPdf, isVideo, isAudio, fileNameWithoutExtension, fileExtension } from '../../utils/fileHandling/files';

const ElMessageDuration = 3000;

export default defineComponent({
	name: 'TheSlider',
	data() {
		return {
			filesDownloading: false,
			isDragging: false,
			downloadWaiting: false,
			deleteWaiting: false,
		};
	},
	components: {
		Swiper,
		SwiperSlide,
		Files,
		TheButton,
		TheBox,
		PdfIcon,
		AudioIcon,
		CloseBold,
	},
	props: {
		files: {
			type: Array as PropType<DetailFile[]>,
			required: true,
		},
		allowDeleteFromServer: {
			type: Boolean,
			default: false,
		},
		downloadable: {
			type: Boolean,
			default: true,
		},
	},
	emits: ['removeFromFiles'],
	computed: {
		texts() {
			return texts;
		},
	},
	methods: {
		downloadZip() {
			const filesToPack = this.files;
			this.packAndDownloadFiles(filesToPack);
		},
		async packAndDownloadFiles(files: DetailFile[]) {
			this.filesDownloading = true;
			const zip = new JSZip();

			for (const file of files.map((f) => f.file)) {
				const fileBlob = await this.fetchFile(file.url);
				zip.file(file.fileName, fileBlob);
			}

			const content = await zip.generateAsync({ type: 'blob' });
			this.filesDownloading = false;
			saveAs(content, 'werberat_archiv.zip');
		},
		async fetchFile(url) {
			const response = await fetch(url);
			if (!response.ok) {
				throw new Error(`Failed to fetch file: ${response.statusText}`);
			}
			const arrayBuffer = await response.arrayBuffer();
			return new Blob([arrayBuffer]);
		},
		async removeFileFromServer(file) {
			if (!file?.file?.id) {
				ElMessage({ message: this.texts.general.deleteFileFailed, type: 'error', duration: ElMessageDuration });
				return;
			}

			this.deleteWaiting = true;

			// deleteCaseFile - also deletes files if it is a statement or complaint
			const deleteResult = await deleteCaseFile(file.file.id);
			if (deleteResult.success) {
				this.$emit('removeFromFiles', file);
				ElMessage({ message: this.texts.general.deleteFileSuccess, type: 'success', duration: ElMessageDuration });
			} else {
				ElMessage({ message: this.texts.general.deleteFileFailed, type: 'error', duration: ElMessageDuration });
			}

			this.deleteWaiting = false;
		},
		getShowUrl,
		isImage,
		isPdf,
		isVideo,
		isAudio,
		fileNameWithoutExtension,
		fileExtension,
	},
});
</script>
